import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { query } from '@ember-data/json-api/request';
import { tracked } from '@glimmer/tracking';
import nameSort from 'scaleflow/utils/nameSort';
import { task, timeout } from 'ember-concurrency';
import {
  objectExtract,
  sortInitiatives,
} from 'scaleflow/components/initiatives/table';
import dateSort from 'scaleflow/utils/dateSort';
import { assert } from '@ember/debug';
import { DateTime } from 'scaleflow/utils/date-time';

export default class BacklogIdeasListController extends Controller {
  @service store;
  @service intl;
  @service currentUser;
  @service('beta-toggle') betaService;
  @service('dropdown') dropdownService;
  @service router;
  @service featureAccess;

  queryParams = [
    'search',
    'grouping',
    'ideaStatuses',
    'sources',
    'themes',
    'departments',
    'areas',
    'customers',
    'submitters',
    'groups',
    'columns',
    'refresher',
  ];

  @tracked search = '';
  @tracked tableSort = '';

  @tracked grouping = null;
  @tracked ideaStatuses = [];
  @tracked themes = [];
  @tracked sources = [];
  @tracked submitters = [];
  @tracked departments = [];
  @tracked areas = [];
  @tracked customers = [];
  @tracked list = [];
  @tracked selectedIdeas = [];
  @tracked groups = [];
  @tracked creatingInitiative = false;
  @tracked processIsOngoing = false;
  @tracked columns = [];
  @tracked refresher = false;

  get sortedIdeas() {
    return this.list.toSorted((a, b) => nameSort(a.title, b.title));
  }

  get ideas() {
    let ideas = this.sortedIdeas;
    if (this.search) {
      ideas = ideas.filter(this.isAMatchCallback);
    }
    return ideas;
  }

  @action
  deleteIdea(idea) {
    idea.destroyRecord();
  }

  get isAMatchCallback() {
    if (!this.search) return () => true;
    return (idea) => {
      return (idea.title ?? '')
        .toLowerCase()
        .includes(this.search.toLowerCase());
    };
  }

  get visibleColumnsCount() {
    return this.columns.length;
  }

  get columnOptions() {
    const columns = [
      {
        label: this.intl.t('dictionary.title'),
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'title',
        showVisibilityToggle: false,
        value: 'title',
      },
      {
        label: this.intl.t('dictionary.status'),
        dropdownDataKey: 'ideaStatuses',
        groupingKey: 'statuses',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'status',
        showVisibilityToggle: true,
        value: 'status',
      },
      {
        label: this.intl.t('dictionary.source', {
          num: 1,
        }),
        showVisibilityToggle: true,
        value: 'source',
      },
      {
        label: this.intl.t('dictionary.submitted-by'),
        dropdownDataKey: 'submitters',
        groupingKey: 'submitters',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'submitter',
        showVisibilityToggle: true,
        value: 'submittedBy',
      },
      {
        label: this.intl.t('dictionary.created-at'),
        showSorting: true,
        sortKey: 'created_at',
        sortType: 'date',
        showVisibilityToggle: true,
        value: 'createdAt',
      },
      {
        label: this.intl.t('department.title', {
          num: 1,
        }),
        dropdownDataKey: 'departments',
        groupingKey: 'departments',
        sortKey: 'department',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        showVisibilityToggle: true,
        value: 'department',
      },
      {
        label: this.intl.t('area.title', {
          num: 3,
        }),
        dropdownDataKey: 'areas',
        groupingKey: 'areas',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'area',
        showVisibilityToggle: true,
        value: 'area',
      },
      {
        label: this.intl.t('idea.deadline'),
        showSorting: true,
        sortType: 'date',
        sortKey: 'deadline',
        showVisibilityToggle: true,
        value: 'deadline',
      },
      {
        label: this.intl.t('dictionary.themes', { num: 2 }),
        dropdownDataKey: 'themes',
        groupingKey: 'themes',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'theme',
        showVisibilityToggle: true,
        value: 'themes',
      },
      {
        label: this.intl.t('customer.title', {
          num: 2,
        }),
        dropdownDataKey: 'customers',
        groupingKey: 'customers',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'customer',
        showVisibilityToggle: true,
        value: 'customers',
      },
      {
        label: this.intl.t('_initiatives.title', {
          num: 2,
        }),
        // dropdownDataKey: 'initiatives',
        groupingKey: 'initiatives',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'initiative',
        showVisibilityToggle: true,
        value: 'initiatives',
      },
      {
        label: this.intl.t('group.title', {
          num: 1,
        }),
        dropdownDataKey: 'groups',
        groupingKey: 'groups',
        showGrouping: true,
        showSorting: true,
        sortType: 'alphabetic',
        sortKey: 'group',
        showVisibilityToggle: true,
        value: 'group',
      },
      {
        label: this.intl.t('idea.revenue-existing'),
        showVisibilityToggle: true,
        value: 'revenueExisting',
      },
      {
        label: this.intl.t('idea.revenue-potential'),
        showVisibilityToggle: true,
        value: 'revenuePotential',
      },
    ];

    columns.forEach((item) => {
      item.show = this.showColumn(item);
    });
    return columns;
  }

  @action
  onSortSelect(key, order) {
    const sort = `${key}|${order}`;
    this.tableSort = this.tableSort === sort ? '' : sort;
  }

  get sortSettings() {
    const [sortKey = '', sortOrder = ''] = this.tableSort.split('|');
    return { sortKey, sortOrder };
  }

  @action
  onFilterSelect(key, e) {
    this[key] = e;
  }

  @action
  anyDropdownFeatureIsAvailable(column) {
    return (
      column.showGrouping ||
      column.showSorting ||
      column.dropdownDataKey ||
      column.showVisibilityToggle
    );
  }

  @action
  showColumn(column) {
    if ((this.columns?.length ?? 0) === 0) {
      if (column.show !== undefined) return column.show;
      return true;
    } else {
      return this.columns.includes(column.value);
    }
  }

  @action
  hideColumn(column, allColumns) {
    let values = [...allColumns];
    if (values.includes(column)) {
      values = values.filter((_column) => _column !== column);
    } else {
      values.push(column);
    }
    this.onColumnSelect(values);
  }

  @action
  onColumnSelect(values) {
    this.columns = values;
  }

  @action
  toggleSelection(id) {
    const selectedIdeas = [...this.selectedIdeas];
    const index = selectedIdeas.findIndex((ideaId) => ideaId === id);
    if (index < 0) selectedIdeas.push(id);
    else selectedIdeas.splice(index, 1);
    this.selectedIdeas = selectedIdeas;
  }

  @action
  toggleGroupSelection(group, e) {
    const ideas = group.content.map((idea) => idea.id);
    let selectedIdeas = [...this.selectedIdeas];

    if (e.target.checked) {
      ideas.forEach((ideaId) => {
        selectedIdeas.push(ideaId);
      });
    } else {
      ideas.forEach((ideaId) => {
        const index = selectedIdeas.findIndex((id) => id === ideaId);
        if (index >= 0) selectedIdeas.splice(index, 1);
      });
    }

    selectedIdeas = Array.from(new Set(selectedIdeas));
    this.selectedIdeas = selectedIdeas;
  }

  @action
  groupIsSelected(group) {
    const ideas = group.content ?? [];
    return ideas.every((idea) => this.selectedIdeas.includes(idea.id));
  }

  @action
  onSearch(value) {
    this.search = value;
  }

  get filters() {
    return {
      status: this.ideaStatuses,
      sources: this.sources,
      themes: this.themes,
      submitter: this.submitters,
      departments: this.departments,
      areas: this.areas,
      customers: this.customers,
      group: this.groups,
    };
  }

  get ideasWithConstraintsApplied() {
    let list = [...this.ideas];
    let groupedSort = this.grouping
      ? this.handleGrouping(list, this.grouping)
      : null;
    const { sortKey, sortOrder } = this.sortSettings;
    switch (sortKey) {
      case 'title': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(a.title, b.title, sortOrder),
              );
            })
          : list.sort((a, b) => nameSort(a.title, b.title, sortOrder));
        break;
      }

      case 'source': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(a.sourceLabel, b.sourceLabel, sortOrder),
              );
            })
          : list.sort((a, b) =>
              nameSort(a.sourceLabel, b.sourceLabel, sortOrder),
            );
        break;
      }

      case 'status': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(a.statusLabel, b.statusLabel, sortOrder),
              );
            })
          : list.sort((a, b) =>
              nameSort(a.statusLabel, b.statusLabel, sortOrder),
            );
        break;
      }

      case 'submitter': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('submitter.name') ?? '',
                  b?.get('submitter.name') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('submitter.name') ?? '',
                b?.get('submitter.name') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'theme': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('firstTheme.title') ?? '',
                  b?.get('firstTheme.title') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('firstTheme.title') ?? '',
                b?.get('firstTheme.title') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'customer': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('firstCustomer.name') ?? '',
                  b?.get('firstCustomer.name') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('firstCustomer.name') ?? '',
                b?.get('firstCustomer.name') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'department': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('firstDepartment.name') ?? '',
                  b?.get('firstDepartment.name') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('firstDepartment.name') ?? '',
                b?.get('firstDepartment.name') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'area': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('firstArea.name') ?? '',
                  b?.get('firstArea.name') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('firstArea.name') ?? '',
                b?.get('firstArea.name') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'initiative': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('firstInitiative.title') ?? '',
                  b?.get('firstInitiative.title') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('firstInitiative.title') ?? '',
                b?.get('firstInitiative.title') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'group': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                nameSort(
                  a?.get('group.title') ?? '',
                  b?.get('group.title') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              nameSort(
                a?.get('group.title') ?? '',
                b?.get('group.title') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'deadline': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                dateSort(
                  a?.get('deadline') ?? '',
                  b?.get('deadline') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              dateSort(
                a?.get('deadline') ?? '',
                b?.get('deadline') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      case 'created_at': {
        groupedSort
          ? Object.keys(groupedSort).forEach((group) => {
              groupedSort[group].content.sort((a, b) =>
                dateSort(
                  a?.get('created_at') ?? '',
                  b?.get('created_at') ?? '',
                  sortOrder,
                ),
              );
            })
          : list.sort((a, b) =>
              dateSort(
                a?.get('created_at') ?? '',
                b?.get('created_at') ?? '',
                sortOrder,
              ),
            );
        break;
      }

      default:
        list.sort((a, b) => nameSort(a.title, b.title));
    }
    return this.grouping ? groupedSort : list;
  }

  @action
  handleGrouping(list, groupKey) {
    switch (groupKey) {
      case 'statuses': {
        const initiatives = sortInitiatives(
          list,
          'No Status',
          'status',
          'status',
          (value) => {
            return objectExtract(this.betaService.ideaStatusOptions, value)
              ?.label;
          },
          'tableOrder',
        );
        const sortedRows = Object.keys(initiatives)
          .sort(this.sortStatus)
          .map((item) => {
            return initiatives[item];
          });
        return sortedRows;
      }

      case 'sources': {
        return sortInitiatives(list, 'No Source', 'source', 'source');
      }

      case 'submitters': {
        return sortInitiatives(list, 'No PM', 'submitter.id', 'submitter.name');
      }

      case 'customers': {
        return sortInitiatives(
          list,
          'No Customer',
          'customer.id',
          'customer.name',
          undefined,
          undefined,
          true,
        );
      }

      case 'departments': {
        return sortInitiatives(
          list,
          'No Department',
          'department.id',
          'department.name',
          undefined,
          undefined,
          true,
        );
      }

      case 'areas': {
        return sortInitiatives(
          list,
          'No Area',
          'area.id',
          'area.name',
          undefined,
          undefined,
          true,
        );
      }

      case 'initiatives': {
        return sortInitiatives(
          list,
          'No Title',
          'firstInitiative.id',
          'firstInitiative.title',
        );
      }

      case 'groups': {
        return sortInitiatives(list, 'No Title', 'group.id', 'group.title');
      }

      case 'themes': {
        return sortInitiatives(
          list,
          'No Theme',
          'theme.id',
          'theme.title',
          undefined,
          undefined,
          true,
        );
      }

      default: {
        return sortInitiatives(list, 'No Status', 'status', 'status');
      }
    }
  }

  @action
  onGroupingSelect(e) {
    this.grouping = this.grouping === e.value ? '' : e.value;
  }

  generateIdeasTextDelta(ideas) {
    const delta = [];

    ideas.forEach((idea, index) => {
      if (index > 0) {
        delta.push({ insert: '\n' });
      }

      delta.push({ insert: 'Idea - ' });
      delta.push({ attributes: { bold: true }, insert: idea.title || '' });
      if (idea.description?.ops && Array.isArray(idea.description.ops)) {
        delta.push({ insert: '\n' });
        delta.push(...idea.description.ops);
      }
      delta.push({ insert: `\nStatus - ${idea.statusLabel}` });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const sourceValue = idea.sourceLabel || 'Not set';
      delta.push({ insert: 'Source - ' });
      delta.push({
        attributes:
          sourceValue === 'Not set' ? { italic: true } : { bold: true },
        insert: sourceValue,
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const submitterName = idea.submitter?.content?.name || 'Unknown';
      delta.push({ insert: 'Submitted by - ' });
      delta.push({
        attributes:
          submitterName === 'Unknown' ? { italic: true } : { bold: true },
        insert: submitterName,
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const createdDate = idea.created_at
        ? DateTime.from(idea.created_at).format('MMM DD, YYYY')
        : '';
      delta.push({ insert: 'Created At - ' });
      if (createdDate) {
        delta.push({ attributes: { bold: true }, insert: createdDate });
      } else {
        delta.push({ attributes: { italic: true }, insert: 'Not set' });
      }
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const departments =
        idea.departments?.content?.map((d) => d.name).join(', ') || '';
      delta.push({ insert: 'Departments - ' });
      delta.push({
        attributes: departments ? { bold: true } : { italic: true },
        insert: departments || 'Not set',
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const areas = idea.areas?.content?.map((a) => a.name).join(', ') || '';
      delta.push({ insert: 'Product modules - ' });
      delta.push({
        attributes: areas ? { bold: true } : { italic: true },
        insert: areas || 'Not set',
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const deadlineValue = idea.deadline
        ? DateTime.from(idea.deadline).format('MMM DD, YYYY')
        : 'Not set';
      delta.push({ insert: 'Deadline - ' });
      delta.push({
        attributes:
          deadlineValue === 'Not set' ? { italic: true } : { bold: true },
        insert: deadlineValue,
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const themes = idea.themes?.content?.map((t) => t.title).join(', ') || '';
      delta.push({ insert: 'Tags - ' });
      delta.push({
        attributes: themes ? { bold: true } : { italic: true },
        insert: themes || 'Not set',
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const customers =
        idea.customers?.content?.map((c) => c.name).join(', ') || '';
      delta.push({ insert: 'Customers - ' });
      delta.push({
        attributes: customers ? { bold: true } : { italic: true },
        insert: customers || 'Not set',
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const revenueExisting = idea.revenue_existing || '';
      delta.push({ insert: 'Revenue existing - ' });
      delta.push({
        attributes: revenueExisting ? { bold: true } : { italic: true },
        insert: revenueExisting || 'Not set',
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });

      const revenuePotential = idea.revenue_potential || '';
      delta.push({ insert: 'Revenue potential - ' });
      delta.push({
        attributes: revenuePotential ? { bold: true } : { italic: true },
        insert: revenuePotential || 'Not set',
      });
      delta.push({ attributes: { list: 'bullet' }, insert: '\n' });
    });

    delta.push({ insert: '\n' });
    return { ops: delta };
  }

  @action
  async createInitiativeFromSelectedIdeas() {
    this.creatingInitiative = true;
    const ideas = this.selectedIdeas;
    if (ideas.length === 0) assert('No ideas were selected');
    const ideaRecords = ideas.map((id) => this.store.peekRecord('idea', id));
    const firstIdea = ideaRecords[0];
    const title = `Idea - ${firstIdea.title}`;
    const summaryDelta = this.generateIdeasTextDelta(ideaRecords);

    const initiative = this.store.createRecord('initiative', {
      title,
      status: 'DRAFT',
      summary: summaryDelta,
      ideas: ideaRecords,
    });
    await initiative.save();
    this.router.transitionTo('backlog.initiatives.initiative', initiative.id);
    this.creatingInitiative = false;
  }

  fetchIdeas = task({ restartable: true, maxConcurrency: 1 }, async () => {
    await timeout(500);
    this.processIsOngoing = true;
    const params = { ...this.filters };
    delete params['columns'];
    delete params['grouping'];
    if (!params.submitter) delete params['submitter'];
    // Like store.query: always fetch, skip unset filters and send arrays as
    // `key[]=`.
    Object.keys(params).forEach((key) => {
      if (params[key] == null) delete params[key];
    });
    const { content } = await this.store.request(
      query('idea', params, {
        reload: true,
        urlParamsSettings: { arrayFormat: 'bracket' },
      }),
    );
    this.list = content.data;
    this.processIsOngoing = false;
  });

  @action
  populateIdeasList() {
    this.fetchIdeas.perform();
  }

  willDestroy() {
    this.processIsOngoing = false;
    super.willDestroy();
  }
}
